import { useMemo, useState } from "react";
import { NoDataContainer } from "../Dashboard.styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FormHelperText, IconButton, MenuItem, Select } from "@mui/material";
import { monthsList, chartColorList } from "../constants";
import {
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Bar
} from "recharts";
import { useAppDispatch } from '../../../hooks/StoreHooks'
import { fetchAveragePointThunk } from "../../../store/thunk/reportingThunk";
import { useAveragePointsData } from "../../../store/selectors/reportingSelectors";
import { useTranslation } from "react-i18next";
import { ActionRow, ChartContainer, NoDataText, OptionContainer, OptionRow, StyledFormControl } from "./Widget.styles";
import { GeneralButton } from "../../../common/ui/GeneralButton";

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());
const takeOptions: number[] = [5, 10, 15, 20]

const AveragePointsChart = () => {
  const { t } = useTranslation();
  const averagePointsData = useAveragePointsData();
  const [year, setYear] = useState<string>(yearOptions[0]);
  const [month, setMonth] = useState<string>(monthsList[0]);
  const [take, setTake] = useState<number>(5);
  const [loading, setLoading] = useState<boolean>(false);
  const [emptyChartData, setEmptyChartData] = useState<boolean>(true);
  const dispatch = useAppDispatch();

  const handleSelection = (setter) => (e) => setter(e.target.value);

  const fetchChartData = () => {
    setLoading(false);
    dispatch(fetchAveragePointThunk({
      year: parseInt(year),
      month: monthsList.indexOf(month) + 1,
      take: take
    })).then(() => {
      setLoading(true);
      setEmptyChartData(false);
    });
  };

  const resetChartData = () => {
    setEmptyChartData(true);
  };

  const formattedChartData = useMemo(() => (
    emptyChartData ?
      [{ name: "", score: 0, fill: "#cccccc" }] :
      averagePointsData.map((elem, i) => ({
        name: `${elem.firstName} ${elem.lastName}`,
        score: elem.averagePoints,
        fill: chartColorList[i % chartColorList.length]
      }))
  ), [averagePointsData, emptyChartData]);

  return (
    <>
      <OptionContainer>
        <OptionRow>
          <StyledFormControl>
            <Select value={year} onChange={handleSelection(setYear)} IconComponent={ExpandMoreIcon}>
              {yearOptions.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
            </Select>
            <FormHelperText>{t("dashboardPage.chooseYear")}</FormHelperText>
          </StyledFormControl>
          <StyledFormControl>
            <Select value={month} onChange={handleSelection(setMonth)} IconComponent={ExpandMoreIcon}>
              {monthsList.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
            </Select>
            <FormHelperText>{t("dashboardPage.chooseMonth")}</FormHelperText>
          </StyledFormControl>
          <StyledFormControl>
            <Select value={take} onChange={handleSelection(setTake)} IconComponent={ExpandMoreIcon}>
              {takeOptions.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
            </Select>
            <FormHelperText>{t("dashboardPage.amountOfUsers")}</FormHelperText>
          </StyledFormControl>
          <IconButton
            onClick={resetChartData}
            aria-label={t("buttons.reset")}
            sx={{ marginRight: 1, color: "#555" }}
            size="small"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </OptionRow>

        <ActionRow>
          <GeneralButton appVariant="widget" onClick={fetchChartData}>
            {t("buttons.apply")}
          </GeneralButton>
        </ActionRow>
      </OptionContainer>

      {loading && formattedChartData.length === 0 ? (
        <NoDataContainer>
          <NoDataText>{t("dashboardPage.noDataAvailable")}</NoDataText>
        </NoDataContainer>
      ) : (
        <ChartContainer>
          <ResponsiveContainer>
            <BarChart
              data={formattedChartData}
              margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" interval={0} angle={-45} textAnchor="end" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="score" fill={formattedChartData[0]?.fill || "#82ca9d"} />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      )}
    </>
  );
};

export default AveragePointsChart